/**
 * Community Preset Service
 * Fetches community presets from the xivdyetools-worker API
 */
/* istanbul ignore file */

import { logger } from '@shared/logger';
import { authService } from './auth-service';
import type { CommunityPreset, PresetFilters, PresetListResponse } from '@xivdyetools/types';

// ============================================
// Types
// ============================================

// CommunityPreset, PresetFilters and PresetListResponse are the
// shared `@xivdyetools/types` contracts, re-exported here so existing
// `@services/community-preset-service` imports keep working unchanged.
export type { CommunityPreset, PresetFilters, PresetListResponse };

export interface CategoryWithCount {
  id: string;
  name: string;
  description: string;
  icon: string | null;
  is_curated: boolean;
  preset_count: number;
}

/**
 * Why a vote failure is a code and not a sentence: this service has no locale,
 * so every English string it used to return was toasted verbatim by
 * `preset-detail` regardless of the user's language. It names the reason; the
 * component looks the text up (`VOTE_ERROR_KEYS` there).
 */
export type VoteErrorCode =
  'notLoggedIn' | 'alreadyVoted' | 'voteFailed' | 'removeVoteFailed' | 'network';

/**
 * Kept local rather than adopted from `@xivdyetools/types`' `VoteResponse`.
 *
 * The real wire response on a 409 (already voted) is
 * `{ success: true, already_voted: true, new_vote_count }` — see
 * `apps/presets-api/src/handlers/votes.ts` `addVote()`, which matches the
 * shared `VoteSuccessResponse` exactly (`already_voted` only exists on the
 * success variant). `voteForPreset()`/`removeVote()` below deliberately
 * report that case as `success: false` instead, so their callers
 * (`preset-detail.ts`, `preset-tool.ts`) route it through their
 * already-voted branch (an info toast, no vote-count update) rather than
 * the success branch (success toast + vote-count update). Both callers must
 * test `already_voted` inside their failure branch: every other failure is
 * `success: false` too (2026-10-04 deep-dive BUG-030). The shared
 * discriminated union has no variant for "success:false with
 * already_voted/new_vote_count", so adopting it here would mean either
 * restructuring those two components' toast logic (out of scope for a
 * type-only refactor) or passing through a `success: true` response that
 * changes which toast fires. Flat and un-discriminated on purpose.
 */
export interface VoteResponse {
  success: boolean;
  new_vote_count: number;
  already_voted?: boolean;
  errorCode?: VoteErrorCode;
  /** The presets-API's own message, when it sent one. Never app-authored copy. */
  error?: string;
}

export interface VoteCheckResponse {
  has_voted: boolean;
  /**
   * Absent whenever there is no real count to report: signed out, a non-OK
   * answer, or no answer at all. BUG-108 (2026-10-04 deep-dive): those paths
   * used to answer 0, which preset-detail applied over the preset's real count.
   */
  vote_count?: number;
}

// ============================================
// Configuration
// ============================================

/**
 * Default API URL - can be overridden via environment or config
 */
const DEFAULT_API_URL = 'https://api.xivdyetools.app';

/**
 * Cache TTL in milliseconds (5 minutes)
 */
const CACHE_TTL = 5 * 60 * 1000;

/**
 * Request timeout in milliseconds
 */
const REQUEST_TIMEOUT = 10000;

// ============================================
// Cache Implementation
// ============================================

interface CacheEntry<T> {
  data: T;
  timestamp: number;
}

class SimpleCache<T> {
  private cache = new Map<string, CacheEntry<T>>();
  private ttl: number;

  constructor(ttl: number = CACHE_TTL) {
    this.ttl = ttl;
  }

  get(key: string): T | null {
    const entry = this.cache.get(key);
    if (!entry) return null;

    if (Date.now() - entry.timestamp > this.ttl) {
      this.cache.delete(key);
      return null;
    }

    return entry.data;
  }

  set(key: string, data: T): void {
    this.cache.set(key, { data, timestamp: Date.now() });
  }

  clear(): void {
    this.cache.clear();
  }

  delete(key: string): void {
    this.cache.delete(key);
  }

  deleteByPrefix(prefix: string): void {
    for (const key of this.cache.keys()) {
      if (key.startsWith(prefix)) this.cache.delete(key);
    }
  }
}

// ============================================
// Service Implementation
// ============================================

/**
 * Community Preset Service
 * Singleton service for fetching community presets from the API
 */
/**
 * An error carrying the HTTP status that produced it.
 *
 * BUG-062: `getPreset()` recovered "not found" by testing whether the error
 * MESSAGE contained '404'. presets-api returns a JSON body for a missing
 * preset, so the message is "Preset not found" and the check never matched --
 * `getPreset()` threw where it had promised to return `null`.
 */
export class PresetApiError extends Error {
  readonly status: number;

  constructor(message: string, status: number) {
    super(message);
    this.name = 'PresetApiError';
    this.status = status;
  }
}

export class CommunityPresetService {
  private static instance: CommunityPresetService | null = null;

  private readonly apiUrl: string;
  private readonly cache: SimpleCache<unknown>;
  private initialized = false;
  private available = false;
  /**
   * Bumped by every invalidation. BUG-031 review follow-up (2026-10-04
   * deep-dive): `deleteByPrefix` only clears what is already stored, so a
   * list request in flight when the cache was invalidated used to write its
   * pre-change answer back afterwards. `request()` caches an answer only when
   * no invalidation ran while it was out.
   */
  private cacheGeneration = 0;
  private authGeneration = 0;
  private authSession: ReturnType<typeof authService.getState>;

  private constructor() {
    // Build-time override only (same as auth-service / preset-submission-
    // service). SECURITY (FINDING-032 / WEB-4): this used to fall back to a
    // `window.PRESET_API_URL` global that nothing set — a markup injection
    // could have defined it by DOM clobbering (`<a id="PRESET_API_URL"
    // href="https://evil…">`) and pointed the bearer-token vote requests at
    // an attacker host. Runtime globals never choose an API origin.
    this.apiUrl = import.meta.env.VITE_PRESETS_API_URL || DEFAULT_API_URL;
    this.cache = new SimpleCache(CACHE_TTL);

    // Vote checks belong to a session. Reading this snapshot does not build
    // auth headers; tokens are still attached only when a request is sent.
    this.authSession = authService.getState();
    authService.subscribe((state) => this.reconcileAuthSession(state));
  }

  private reconcileAuthSession(state: ReturnType<typeof authService.getState>): void {
    if (
      state.isAuthenticated !== this.authSession.isAuthenticated ||
      state.token !== this.authSession.token ||
      state.user?.id !== this.authSession.user?.id ||
      state.provider !== this.authSession.provider
    ) {
      this.authGeneration++;
      this.cache.deleteByPrefix('vote:');
    }
    this.authSession = state;
  }

  /**
   * Get singleton instance
   */
  static getInstance(): CommunityPresetService {
    if (!CommunityPresetService.instance) {
      CommunityPresetService.instance = new CommunityPresetService();
    }
    return CommunityPresetService.instance;
  }

  /**
   * Initialize the service and check API availability
   * BUG-015 FIX: Added Promise.race fallback timeout for browsers
   * where AbortController might not work reliably
   */
  async initialize(): Promise<boolean> {
    if (this.initialized) {
      return this.available;
    }

    try {
      // BUG-015: Use Promise.race as a fallback timeout mechanism
      // This ensures timeout works even if AbortController isn't fully supported
      const healthCheckPromise = this.fetchWithTimeout(`${this.apiUrl}/health`, {
        method: 'GET',
      });

      const timeoutPromise = new Promise<Response>((_, reject) =>
        setTimeout(
          () => reject(new Error('Health check timeout (fallback)')),
          REQUEST_TIMEOUT + 1000
        )
      );

      const response = await Promise.race([healthCheckPromise, timeoutPromise]);

      this.available = response.ok;
      this.initialized = true;

      if (this.available) {
        logger.info('CommunityPresetService: API available');
      } else {
        logger.warn('CommunityPresetService: API returned non-OK status');
      }
    } catch (error) {
      logger.warn('CommunityPresetService: API not available', error);
      this.available = false;
      this.initialized = true;
    }

    return this.available;
  }

  /**
   * Check if the service is available
   */
  isAvailable(): boolean {
    return this.available;
  }

  /**
   * Fetch with timeout
   */
  private async fetchWithTimeout(
    url: string,
    options: RequestInit = {},
    timeout: number = REQUEST_TIMEOUT
  ): Promise<Response> {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeout);

    try {
      const response = await fetch(url, {
        ...options,
        signal: controller.signal,
      });
      return response;
    } finally {
      clearTimeout(timeoutId);
    }
  }

  /**
   * Make API request with error handling
   */
  private async request<T>(path: string, cacheKey?: string): Promise<T> {
    // Check cache first
    if (cacheKey) {
      const cached = this.cache.get(cacheKey) as T | null;
      if (cached) {
        return cached;
      }
    }

    const url = `${this.apiUrl}${path}`;
    const generation = this.cacheGeneration;

    try {
      const response = await this.fetchWithTimeout(url);

      if (!response.ok) {
        const errorData = (await response.json().catch(() => ({}))) as { message?: string };
        // BUG-062: the status has to travel WITH the error. Callers used to
        // recover it by string-matching the message for '404', which never
        // matched: presets-api answers a missing preset with a JSON body, so
        // `errorData.message` is "Preset not found" and the
        // `API request failed: 404` fallback only fires for a bodyless error.
        throw new PresetApiError(
          errorData.message || `API request failed: ${response.status}`,
          response.status
        );
      }

      const data = (await response.json()) as T;

      // Cache successful response, unless the cache was invalidated while
      // this request was in flight: then the answer may predate the change.
      if (cacheKey && generation === this.cacheGeneration) {
        this.cache.set(cacheKey, data);
      }

      return data;
    } catch (error) {
      if (error instanceof Error && error.name === 'AbortError') {
        throw new Error('Request timeout', { cause: error });
      }
      throw error;
    }
  }

  // ============================================
  // Public API Methods
  // ============================================

  /**
   * Get presets with optional filtering
   */
  async getPresets(filters: PresetFilters = {}): Promise<PresetListResponse> {
    const params = new URLSearchParams();

    if (filters.category) params.set('category', filters.category);
    if (filters.search) params.set('search', filters.search);
    if (filters.status) params.set('status', filters.status);
    if (filters.sort) params.set('sort', filters.sort);
    if (filters.page) params.set('page', String(filters.page));
    if (filters.limit) params.set('limit', String(filters.limit));
    if (filters.is_curated !== undefined) params.set('is_curated', String(filters.is_curated));

    const query = params.toString();
    const path = `/api/v1/presets${query ? `?${query}` : ''}`;
    const cacheKey = `presets:${query}`;

    return this.request<PresetListResponse>(path, cacheKey);
  }

  /**
   * Get a single preset by ID
   */
  async getPreset(id: string): Promise<CommunityPreset | null> {
    try {
      return await this.request<CommunityPreset>(
        `/api/v1/presets/${encodeURIComponent(id)}`,
        `preset:${id}`
      );
    } catch (error) {
      if (error instanceof PresetApiError && error.status === 404) {
        return null;
      }
      throw error;
    }
  }

  /**
   * Get all categories with preset counts
   */
  async getCategories(): Promise<CategoryWithCount[]> {
    const response = await this.request<{ categories: CategoryWithCount[] }>(
      '/api/v1/categories',
      'categories'
    );
    return response.categories;
  }

  /**
   * Clear all cached data
   */
  clearCache(): void {
    this.cacheGeneration++;
    this.cache.clear();
    logger.info('CommunityPresetService: Cache cleared');
  }

  /**
   * Drop every cached preset list, and the cached copy of `presetId` when
   * given. Call after anything that changes what a list shows: a submit, an
   * edit, a preview-image upload or removal, a delete or a vote. A request
   * already in flight is not cached when it lands (see `cacheGeneration`).
   *
   * BUG-031 (2026-10-04 deep-dive): lists are cached per query for 5 minutes,
   * and the only invalidation was per-id, after a vote. So preset-tool's
   * reload after a delete (same search, same sort, same cache key) got the
   * pre-delete list back, deleted preset included.
   */
  invalidatePresets(presetId?: string): void {
    this.cacheGeneration++;
    this.cache.deleteByPrefix('presets:');
    if (presetId) this.cache.delete(`preset:${presetId}`);
  }

  // ============================================
  // Voting Methods
  // ============================================

  /**
   * Vote for a preset
   * Requires authentication
   */
  async voteForPreset(presetId: string): Promise<VoteResponse> {
    if (!authService.isAuthenticated()) {
      return {
        success: false,
        new_vote_count: 0,
        errorCode: 'notLoggedIn',
      };
    }

    try {
      const response = await this.fetchWithTimeout(
        `${this.apiUrl}/api/v1/votes/${encodeURIComponent(presetId)}`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...authService.getAuthHeaders(),
          },
        }
      );

      const data = (await response.json()) as VoteResponse;

      if (response.status === 409) {
        // Already voted
        return {
          success: false,
          new_vote_count: data.new_vote_count || 0,
          already_voted: true,
          errorCode: 'alreadyVoted',
        };
      }

      if (!response.ok) {
        return {
          success: false,
          new_vote_count: 0,
          errorCode: 'voteFailed',
          error: (data as { message?: string }).message,
        };
      }

      // The count changed in this preset and in every list that carries it
      this.invalidatePresets(presetId);
      this.cache.delete(`vote:${presetId}`);

      logger.info(`Voted for preset ${presetId}`);
      return data;
    } catch (error) {
      logger.error('Error voting for preset:', error);
      return {
        success: false,
        new_vote_count: 0,
        errorCode: 'network',
      };
    }
  }

  /**
   * Remove vote from a preset
   * Requires authentication
   */
  async removeVote(presetId: string): Promise<VoteResponse> {
    if (!authService.isAuthenticated()) {
      return {
        success: false,
        new_vote_count: 0,
        errorCode: 'notLoggedIn',
      };
    }

    try {
      const response = await this.fetchWithTimeout(
        `${this.apiUrl}/api/v1/votes/${encodeURIComponent(presetId)}`,
        {
          method: 'DELETE',
          headers: {
            ...authService.getAuthHeaders(),
          },
        }
      );

      if (!response.ok) {
        const data = (await response.json().catch(() => ({}))) as { message?: string };
        return {
          success: false,
          new_vote_count: 0,
          errorCode: 'removeVoteFailed',
          error: data.message,
        };
      }

      const data = (await response.json()) as VoteResponse;

      // The count changed in this preset and in every list that carries it
      this.invalidatePresets(presetId);
      this.cache.delete(`vote:${presetId}`);

      logger.info(`Removed vote from preset ${presetId}`);
      return data;
    } catch (error) {
      logger.error('Error removing vote:', error);
      return {
        success: false,
        new_vote_count: 0,
        errorCode: 'network',
      };
    }
  }

  /**
   * Check if user has voted for a preset
   * Requires authentication
   */
  async hasVoted(presetId: string): Promise<VoteCheckResponse> {
    // No path without an answer from the API reports a vote_count (BUG-108).
    if (!authService.isAuthenticated()) {
      return { has_voted: false };
    }

    // Initial storage restoration does not emit an auth notification. Capture
    // that session before consulting the cache so its later logout is seen.
    this.reconcileAuthSession(authService.getState());

    // Check cache first
    const cacheKey = `vote:${presetId}`;
    const cached = this.cache.get(cacheKey) as VoteCheckResponse | null;
    if (cached) {
      return cached;
    }

    const generation = this.cacheGeneration;
    const authGeneration = this.authGeneration;
    try {
      const response = await this.fetchWithTimeout(
        `${this.apiUrl}/api/v1/votes/${encodeURIComponent(presetId)}/check`,
        {
          method: 'GET',
          headers: {
            ...authService.getAuthHeaders(),
          },
        }
      );

      if (!response.ok) {
        return { has_voted: false };
      }

      const data = (await response.json()) as VoteCheckResponse;

      // A delayed check must not reinsert pre-mutation or another session's
      // answer after the cache has been invalidated (BUG-014).
      if (generation === this.cacheGeneration && authGeneration === this.authGeneration) {
        this.cache.set(cacheKey, data);
      }

      return data;
    } catch (error) {
      logger.error('Error checking vote status:', error);
      return { has_voted: false };
    }
  }
}

// Export singleton instance
export const communityPresetService = CommunityPresetService.getInstance();
